import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Mail, Phone, User, MapPin, Eye, EyeOff } from 'lucide-react'
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import TopNavigation from '../../../components/TopNavigation.jsx'

const RegisterPage: React.FC = () => {
  const { signUp, signUpWithPhone } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const [method, setMethod] = useState<'email' | 'phone'>('email')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    zipCode: ''
  })

  // Handle URL parameters for method selection
  useEffect(() => {
    const t = searchParams.get('t')
    if (t === 'mobile') {
      setMethod('phone')
    } else if (t === 'email') {
      setMethod('email')
    }
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      alert('Password must be at least 8 characters')
      return
    }

    setLoading(true)

    try {
      const userData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        zip_code: formData.zipCode
      }

      if (method === 'email') {
        await signUp(formData.email, formData.password, userData)
        // Redirect to dedicated verification page
        navigate(`/verify?email=${encodeURIComponent(formData.email)}`)
      } else {
        // Validate and format phone number to E.164 format
        if (!isValidPhoneNumber(formData.phone, 'US')) {
          alert('Please enter a valid US phone number (e.g., +1 555-123-4567)')
          setLoading(false)
          return
        }

        const phoneNumber = parsePhoneNumber(formData.phone, 'US')
        const e164Phone = phoneNumber.format('E.164') // Converts to +1XXXXXXXXXX

        await signUpWithPhone(e164Phone, formData.password, userData)
        // Redirect to dedicated phone verification page
        console.log('[RegisterPage] Navigating to verify-phone with:', e164Phone)
        navigate(`/verify-phone?phone=${encodeURIComponent(e164Phone)}`)
      }
    } catch (error) {
      console.error('Registration error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="min-h-screen-safe bg-background flex flex-col">
      {/* Header */}
      <TopNavigation />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-2">Join SECRET</h1>
            <p className="text-muted-foreground">Create your exclusive access account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Registration Method Toggle */}
            <div className="flex rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => setMethod('email')}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  method === 'email'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Mail size={16} className="inline mr-2" />
                Email
              </button>
              <button
                type="button"
                onClick={() => setMethod('phone')}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  method === 'phone'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Phone size={16} className="inline mr-2" />
                Phone
              </button>
            </div>

            {/* Contact Field */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {method === 'email' ? 'Email Address' : 'Phone Number'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {method === 'email' ? (
                    <Mail size={20} className="text-muted-foreground" />
                  ) : (
                    <Phone size={20} className="text-muted-foreground" />
                  )}
                </div>
                <input
                  type={method === 'email' ? 'email' : 'tel'}
                  value={method === 'email' ? formData.email : formData.phone}
                  onChange={(e) => handleInputChange(method, e.target.value)}
                  placeholder={method === 'email' ? 'your@email.com' : '+1 (555) 123-4567'}
                  className="w-full pl-12 pr-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  required
                />
              </div>
              {/* SMS Opt-in Disclosure for Phone Registration */}
              {method === 'phone' && (
                <p className="mt-2 text-xs text-muted-foreground">
                  By signing up, you agree to receive SMS notifications from Tailtations SECRET program. Standard message and data rates may apply.
                </p>
              )}
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">First Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={20} className="text-muted-foreground" />
                  </div>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="John"
                    className="w-full pl-12 pr-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Doe"
                  className="w-full px-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Zip Code */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Zip Code</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin size={20} className="text-muted-foreground" />
                </div>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  placeholder="90210"
                  className="w-full pl-12 pr-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="w-full px-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Confirm Password</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                placeholder="Re-enter your password"
                className="w-full px-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                required
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full flex items-center justify-center px-6 py-3 text-sm font-medium rounded-md transition-colors disabled:opacity-50"
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <span>Create SECRET Account</span>
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6">
            <div className="flex justify-center">
              <span className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
                  Sign in here
                </Link>
              </span>
            </div>
          </div>

          {/* Terms */}
          <p className="text-xs text-muted-foreground text-center mt-6">
            By creating an account, you agree to our Terms of Service and Privacy Policy.
            SECRET membership is exclusive to verified users.
          </p>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage